import React from 'react';
import range from 'lodash/range';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { withStyles } from '@material-ui/core/styles';
import { toastError } from 'bench-internal-theme';
import PropTypes from 'prop-types';
import {
  Button,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { format } from 'date-fns';
import memoize from 'memoize-one';
import moment from 'moment';
import SwitchControl from './SwitchControl';
import {
  ledgerAccountCategories,
  collectionMethods,
  optimizationOptions,
  volumeOptions,
  formatWithOrdinal,
  DEFAULT_ACCOUNT_TYPE,
  findFdsAccountByLedgerId,
  DEFAULT_VOLUME,
  optimizedStatus,
  thirdPartyLoginStatus,
} from './util';
import MUISelect from './MUISelect';
import FunctionalAccountDetails from './FunctionalAccountDetails';
import MonthYearSelect from './MonthYearSelect';
import SplitLedgerCollectionMethods from './SplitLedgerCollectionMethods';
import { FormStyles, styles } from './LedgerForm.styles';

// We don't have a form validation library besides gandalf-validator in this repo.
// This can easily be moved to react-hook-form in the future by moving these into register().
// Validate functions must return a boolean
// Validation message is returned with the validate function
const fieldValidation = {
  name: {
    required: true,
    validate: value => value !== '',
    validationMessage: 'Name cannot be empty.',
  },
  details: {
    required: false,
    validate: value => value.length < 1000,
    validationMessage: 'Cannot be more than 1000 characters',
  },
  currencyId: { required: true },
  categoryId: { required: true },
  institution: {
    required: false,
    validate: (value, formState) =>
      formState.isFunctional &&
      !formState.isTemporary &&
      !formState.alreadyExistWithoutFinancialInstitution
        ? !!value
        : true,
    validationMessage: 'Institution must be defined for a functional ledger',
  },
  productType: {
    required: false,
    validate: (value, formState) =>
      formState.isFunctional && !formState.isTemporary && !formState.alreadyExistWithoutProductType
        ? !!value
        : true,
    validationMessage: 'Product Type must be defined for a functional ledger',
  },
  volume: {
    required: false,
    validate: (value, formState) => (formState.isFunctional ? !!value : true),
    validationMessage: 'This field is required',
  },
  dateActive: {
    required: false,
    validate: (value, formState) => (formState.isFunctional ? !!value : true),
    validationMessage: 'This field is required',
  },
  dateInactive: {
    required: false,
    validate: (value, formState) =>
      formState.isFunctional && value && formState.dateActive
        ? new Date(value) >= new Date(formState.dateActive)
        : true,
    validationMessage: 'Inactive date cannot be lesser than active date',
  },
  statementDayOfMonth: {
    required: false,
    validate: (value, formState) => (formState.isFunctional ? !!value : true),
    validationMessage: 'This field is required',
  },
};

class LedgerForm extends React.Component {
  constructor(props) {
    super(props);
    const { ledgerId, ledgerAccount, referenceData } = props;
    const selectedFDSIntegration = findFdsAccountByLedgerId(
      referenceData?.client?.financialDataConnections,
      ledgerId,
    );
    this.state = {
      name: ledgerAccount?.name || '',
      details: ledgerAccount?.details || '',
      isTemporary: ledgerAccount?.isTemporary || false,
      currencyId: this.getCurrency(),
      isFunctional: !!ledgerAccount?.role || false,
      categoryId: ledgerAccount?.category.id || '1',
      institution: this.getAsOption(
        ledgerAccount?.financialInstitutionProduct?.financialInstitution,
      ),
      productType: this.getAsOption(
        ledgerAccount?.financialInstitutionProduct?.financialInstitutionProductType,
      ),
      financialInstitutionProductId: ledgerAccount?.financialInstitutionProduct?.id,
      statementDayOfMonth: ledgerAccount?.statementDayOfMonth,
      dateActive: ledgerAccount?.accountSettings?.dateActive,
      dateInactive: ledgerAccount?.accountSettings?.dateInactive || null,
      collectionMethod: ledgerAccount?.accountSettings?.collectionMethod,
      optimizationStatus: ledgerAccount?.accountSettings?.optimizationStatus,
      integration: selectedFDSIntegration?.id || '',
      thirdPartyLoginAvailable: ledgerAccount?.thirdPartyLoginAvailable
        ? thirdPartyLoginStatus.AVAILABLE
        : thirdPartyLoginStatus.UNAVAILABLE,
      volume: ledgerAccount?.accountSettings?.volume || DEFAULT_VOLUME,
      autoPostEnabled: ledgerAccount ? ledgerAccount.autoPostEnabled : true,
      role: ledgerAccount ? this.props.ledgerAccount.role : null,
      accountType: ledgerAccount?.accountType || DEFAULT_ACCOUNT_TYPE,
      isAutoGenerated: ledgerAccount?.isAutoGenerated || false,
      accountLastFourDigits: ledgerAccount?.accountLastFourDigits || null,
      errors: {},
      canAutoPopulateName: !this.props.ledgerId,
      // todo hopefully remove one day
      // these 2 states below determine whether or not to bypass validations
      // for existing ledger accounts without required data. don't want to make a
      // habit of bypassing validations, but need this for now as there are a lot of
      // ledger accounts that will not pass current validations
      alreadyExistWithoutProductType:
        ledgerAccount &&
        !ledgerAccount.financialInstitutionProduct?.financialInstitutionProductType,
      alreadyExistWithoutFinancialInstitution: ledgerAccount && !ledgerAccount.financialInstitution,
    };
  }

  componentDidMount() {
    if (!this.props.ledgerId || !this.state.isFunctional) {
      this.setFunctionalDefaults();
    }
  }

  onIsFunctionalChange() {
    if (this.props.ledgerId) return;
    this.setFunctionalDefaults();
    let details;
    if (!this.state.details && ['6', '7', '8'].includes(this.state.categoryId)) {
      details = 'Use for automation with payroll or invoicing providers';
      this.setState({ details });
    }
  }

  getCurrency = () => {
    if (!this.props.ledgerId) {
      return this.props.referenceData.client.currencyId;
    }
    // better to use the client currency as a last resort as opposed to something fixed like USD
    return this.props.ledgerAccount?.currencyId || this.props.referenceData.client.currencyId;
  };

  getLedgerPurpose = () => {
    if (!this.props.ledgerId) return 'Customized';

    const {
      ledgerAccount,
      referenceData: { templateLedgers, client },
    } = this.props;

    const templateLedger = templateLedgers?.find(
      template =>
        ledgerAccount.code === template.code &&
        (client.countryCode === template.country?.code || template.country?.code === null),
    );
    return templateLedger ? templateLedger.name : 'Customized';
  };

  getAsOption = data => {
    if (!data) return undefined;
    return { value: data?.id, label: data?.name };
  };

  setFunctionalDefaults = () => {
    let { statementDayOfMonth, dateActive, volume } = this.state;
    if (!statementDayOfMonth) {
      statementDayOfMonth = '1';
    }
    if (!dateActive) {
      dateActive = format(new Date(), 'YYYY-MM');
    }
    if (!volume) {
      volume = 'STANDARD';
    }
    this.setState({
      statementDayOfMonth,
      dateActive,
      volume,
    });
  };

  getNamePrefix = () => {
    // if category is Equity & product type is Checking (Business Personal Crossover), Savings (Business Personal Crossover) or Credit Card (Business Personal Crossover)
    if (
      this.state.categoryId === '5' &&
      ['10', '11', '12'].includes(this.state.productType.value)
    ) {
      return 'P/B';
    }
    return '';
  };

  currencyOptions = memoize(() =>
    this.props.referenceData.currencies?.map(currency => ({
      id: currency.id,
      label: currency.code,
    })),
  )();

  ledgerAccountCategoryOptions = memoize(() =>
    ledgerAccountCategories.map(category => ({
      id: category.id,
      label: category.name,
    })),
  )();

  collectionTypeOptions = memoize(() =>
    collectionMethods.map(method => ({
      id: method,
      label: method,
    })),
  )();

  optimizationOptionsFormatted = memoize(() =>
    optimizationOptions.map(option => ({
      id: option,
      label: option,
    })),
  )();

  statementDateRangeOptions = memoize(() =>
    range(1, 32).map(day => ({
      id: day.toString(),
      label: formatWithOrdinal(day),
    })),
  )();

  handleChange = event => {
    if (!event?.target?.name) return;
    const { name, value } = event.target;
    this.setState({
      [name]: value,
    });
  };

  handleCurriedChange = name => value => {
    this.setState(
      {
        [name]: value,
      },
      this.autoSetName,
    );
  };

  autoSetName = () => {
    const { canAutoPopulateName, institution, productType, accountLastFourDigits } = this.state;
    // Clear formatted name if institution changes or product value not set for new ledgers
    if ((institution?.label || !productType?.label) && canAutoPopulateName) {
      this.setState({
        name: '',
      });
    }
    if (canAutoPopulateName && institution?.label && productType?.label) {
      let name;
      const simpleProductType = productType.label.split(' (')[0];
      // Only use the ProductType as the ledger name for non-default product types
      if (productType.isDefault) {
        const namePrefix = this.getNamePrefix();
        const nameToFormat = [institution.label.split('-')[0].trim(), simpleProductType];
        if (accountLastFourDigits?.length) {
          nameToFormat.push(accountLastFourDigits);
        }
        name = nameToFormat.join(' - ');
        if (namePrefix) name = `${namePrefix} - ${name}`;
      } else {
        name = simpleProductType;
      }
      this.setState({
        name,
      });
    }
  };

  handleSwitchChange = ({ name, value }) => {
    this.setState({
      [name]: value,
    });

    if (name === 'isFunctional' && value) {
      this.onIsFunctionalChange();
    }
  };

  handleSubmit = (event, isSaveAndFinish) => {
    event.preventDefault();
    this.validateFieldsAndSubmit(isSaveAndFinish);
  };

  isFunctionalLedgerToggleDisabled = () => {
    if (this.state.role) return false;
    return this.state.accountType !== 'FLAVOUR';
  };

  showError = errors => this.setState({ errors });

  validateFieldsAndSubmit = isSaveAndFinish => {
    const isFunctionalAndTemporary = this.state.isFunctional && this.state.isTemporary;
    const errors = {};
    Object.keys(fieldValidation).forEach(fieldKey => {
      const fieldOptions = fieldValidation[fieldKey];
      const stateValue = this.state[fieldKey];

      if (fieldOptions.required && !stateValue) {
        errors[fieldKey] = { error: 'This field is required' };
      }

      if (fieldOptions.validate && !errors[fieldKey]) {
        if (!fieldOptions.validate(stateValue, this.state)) {
          errors[fieldKey] = { error: fieldOptions.validationMessage || 'This field is invalid.' };
        }
      }
    });

    if (Object.keys(errors).length) {
      this.setState({ errors });
      if (isFunctionalAndTemporary) {
        toastError(
          'Missing required fields. Toggle off temporary to see which fields need to be addressed.',
        );
      }
    } else {
      this.props.onFormSubmit(this.state, isSaveAndFinish);
    }
  };

  render() {
    const { classes, isQuickAdd, loading, closeModal, clientId } = this.props;
    const {
      name,
      details,
      isTemporary,
      currencyId,
      isFunctional,
      categoryId,
      institution,
      productType,
      statementDayOfMonth,
      dateActive,
      dateInactive,
      collectionMethod,
      optimizationStatus,
      integration,
      thirdPartyLoginAvailable,
      volume,
      autoPostEnabled,
      isAutoGenerated,
      accountLastFourDigits,
      errors,
    } = this.state;

    return (
      <FormStyles>
        <TextField
          id="name"
          name="name"
          label="Ledger Name"
          fullWidth
          className={classes.textField}
          value={name}
          onChange={this.handleChange}
          variant="filled"
          onKeyUp={event => this.setState({ canAutoPopulateName: !event.target.value })}
          error={!!errors.name}
          helperText={errors.name?.error}
        />
        <Typography className={classes.helperText}>
          Ledger purpose: {this.getLedgerPurpose()}
        </Typography>
        {isAutoGenerated ? (
          <Typography className={classes.helperText}>
            Ledger Creation Method: System Generated
          </Typography>
        ) : null}
        {!isQuickAdd ? (
          <TextField
            id="details"
            name="details"
            label="Notes"
            fullWidth
            multiline
            helperText={`${details.length} / 1000`}
            inputProps={{
              maxLength: 1000,
            }}
            className={classes.textField}
            value={details}
            onChange={this.handleChange}
            variant="filled"
          />
        ) : null}
        <Grid container style={{ padding: 0 }}>
          <Grid item xs={3}>
            <SwitchControl
              id="isTemporary"
              name="isTemporary"
              label={
                <div className={classes.temporaryLedgerLabelContainer}>
                  <span>Is Temporary</span>
                  <Tooltip
                    className={classes.temporaryLedgerTooltip}
                    placement="top-start"
                    title="Use temporary ledgers to support your workflow. Helpful for tidying up accounts, showing specific transactions to clients. These must be removed before creating a YEF"
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </div>
              }
              value={isTemporary}
              onChange={this.handleSwitchChange}
            />
          </Grid>
          <Grid item xs={3}>
            <MUISelect
              id="currencyId"
              name="currencyId"
              label="Currency"
              disabled
              value={currencyId}
              onChange={this.handleChange}
              options={this.currencyOptions}
            />
          </Grid>
          <Grid item xs={3}>
            <SwitchControl
              id="isFunctional"
              name="isFunctional"
              label="Functional Ledger"
              value={isFunctional}
              onChange={this.handleSwitchChange}
              disabled={this.isFunctionalLedgerToggleDisabled()}
            />
          </Grid>
          {isQuickAdd ? (
            <>
              <FormLabel component="legend" xs={4}>
                Category
              </FormLabel>
              <RadioGroup
                name="categoryId"
                className={classes.categoryRadioGroup}
                onChange={this.handleChange}
                value={categoryId}
              >
                {this.ledgerAccountCategoryOptions.map(option => (
                  <FormControlLabel
                    className={classes.categoryRadioControl}
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={option.label}
                    aria-label={option.label}
                  />
                ))}
              </RadioGroup>
            </>
          ) : (
            <Grid item xs={3}>
              <MUISelect
                id="categoryId"
                name="categoryId"
                label="Category"
                onChange={this.handleChange}
                value={categoryId}
                options={this.ledgerAccountCategoryOptions}
                placeholder="Select category"
                errors={errors}
              />
            </Grid>
          )}
        </Grid>

        {/* Institution details are shown in Quick Add when a ledger is functional */}
        {!isTemporary && isFunctional ? (
          <FunctionalAccountDetails
            formValues={{ institution, productType, accountLastFourDigits }}
            ledgerCategoryId={categoryId}
            onChange={this.handleCurriedChange}
            onUpdateProduct={this.handleCurriedChange('financialInstitutionProductId')}
            errors={errors}
            classes={{ temporaryLedgerLabelContainer: classes.temporaryLedgerLabelContainer }}
            clientId={clientId}
            showErrors={this.showError}
          />
        ) : null}
        {!isQuickAdd && !isTemporary && isFunctional ? (
          <>
            <Grid container style={{ marginTop: '1.8rem' }}>
              <Grid item xs={12} className={classes.sectionHeader}>
                <Typography>Ledger Dates</Typography>
              </Grid>
              <Grid item xs={4}>
                <MUISelect
                  id="statementDayOfMonth"
                  name="statementDayOfMonth"
                  label="Statement Date"
                  value={statementDayOfMonth}
                  onChange={this.handleChange}
                  options={this.statementDateRangeOptions}
                  inputDataTestId="statementDayOfMonth-input"
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <MonthYearSelect
                  name="dateActive"
                  value={dateActive}
                  label="Date Active"
                  onDateChange={this.handleCurriedChange('dateActive')}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={4}>
                <MonthYearSelect
                  name="dateInactive"
                  value={dateInactive}
                  label="Date Inactive"
                  onDateChange={this.handleCurriedChange('dateInactive')}
                  errors={errors}
                />
              </Grid>
            </Grid>
            <Grid container style={{ marginTop: '1.8rem' }}>
              <Grid item xs={12} className={classes.sectionHeader}>
                <Typography>Collections & Integrations</Typography>
              </Grid>
              <Grid item xs={4}>
                <MUISelect
                  id="collectionMethod"
                  name="collectionMethod"
                  label="Collection Type"
                  // Setting an empty string by default allows the placeholder to show, while
                  // still allowing the state to be undefined.
                  value={collectionMethod || ''}
                  onChange={this.handleChange}
                  options={this.collectionTypeOptions}
                  placeholder="Select method"
                />
              </Grid>
              <Grid item xs={4}>
                <MUISelect
                  id="optimizationStatus"
                  name="optimizationStatus"
                  label="Optimization"
                  value={optimizationStatus || ''}
                  onChange={this.handleChange}
                  options={this.optimizationOptionsFormatted}
                  placeholder="Select status"
                />
              </Grid>
            </Grid>

            <SplitLedgerCollectionMethods
              clientId={clientId}
              toDate={moment().format('YYYY-MM-DD')}
              formValues={{
                integration,
                thirdPartyLoginAvailable,
              }}
              onChange={event => {
                this.handleChange(event);
                // Toggle optimizationStatus to OPTIMIZED if an integration or 3PL is available
                if (event.target.value) {
                  if (
                    event.target.name === 'thirdPartyLoginAvailable' &&
                    event.target.value === thirdPartyLoginStatus.UNAVAILABLE
                  ) {
                    return;
                  }
                  this.handleCurriedChange('optimizationStatus')(optimizedStatus);
                }
              }}
            />

            <Grid container style={{ marginTop: '1.8rem' }}>
              <Grid item xs={12} className={classes.sectionHeader}>
                <Typography>Other</Typography>
              </Grid>
              <Grid item xs={4}>
                <MUISelect
                  id="volume"
                  name="volume"
                  label="Volume"
                  value={volume || ''}
                  onChange={this.handleChange}
                  options={volumeOptions}
                  placeholder="Select volume"
                  errors={errors}
                />
              </Grid>
              <div className={classes.temporaryLedgerLabelContainer}>
                <SwitchControl
                  id="autoPostEnabled"
                  name="autoPostEnabled"
                  onChange={this.handleSwitchChange}
                  value={autoPostEnabled}
                  label="Autopost"
                />
                <Tooltip
                  className={classes.temporaryLedgerTooltip}
                  placement="top-start"
                  title="Auto-posting will only occur for clients with an inbox state of keep up or catch up"
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
            </Grid>
          </>
        ) : null}
        <Grid container justifyContent="flex-end" spacing={1}>
          <Grid item>
            <Button
              type="button"
              className={classes.button}
              disabled={loading}
              onClick={closeModal}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
              classes={{ label: classes.saveButtonLabel }}
              disabled={loading}
              onClick={e => this.handleSubmit(e, false)}
            >
              {loading ? 'Saving' : 'Save'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              classes={{ label: classes.saveFinishButtonLabel }}
              disabled={loading}
              onClick={e => this.handleSubmit(e, true)}
            >
              {'Save & Finish'}
            </Button>
          </Grid>
        </Grid>
      </FormStyles>
    );
  }
}

LedgerForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  isQuickAdd: PropTypes.bool,
  onFormSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  ledgerId: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  ledgerAccount: PropTypes.object,
  referenceData: PropTypes.shape({
    templateLedgers: PropTypes.arrayOf(PropTypes.object),
    currencies: PropTypes.arrayOf(PropTypes.object),
    client: PropTypes.object,
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
};

LedgerForm.defaultProps = {
  ledgerId: undefined,
  isQuickAdd: false,
  ledgerAccount: undefined,
};

export default withStyles(styles)(LedgerForm);
